import { useState, useEffect, useCallback } from 'react';
import { allergiesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Plus, AlertTriangle, Trash2, ShieldAlert, Pill, Leaf, Bug } from 'lucide-react';
import { cn } from '../lib/utils';

const TYPES = ['Drug', 'Food', 'Environmental', 'Latex', 'Other'];
const SEVERITIES = ['Mild', 'Moderate', 'Severe', 'Life-threatening'];
const SEV_STYLES = {
  Mild: 'bg-green-100 text-green-700',
  Moderate: 'bg-amber-100 text-amber-700',
  Severe: 'bg-orange-100 text-orange-700',
  'Life-threatening': 'bg-red-100 text-red-700',
};
const TYPE_ICONS = { Drug: Pill, Food: Leaf, Environmental: Bug, Latex: ShieldAlert, Other: AlertTriangle };

const AllergiesPage = () => {
  const { hospital } = useAuth();
  const [patientId, setPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [allergies, setAllergies] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');

  // Load all patients for the selector
  useEffect(() => {
    const loadPatients = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/patients/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        setAllPatients(Array.isArray(data) ? data : data.patients || []);
      } catch { /* ignore */ }
    };
    loadPatients();
  }, []);

  const loadAllergies = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await allergiesAPI.getByPatient(pid);
      setAllergies(res.data || []);
    } catch { toast.error('Failed to load allergies'); }
    setLoading(false);
  }, []);

  const selectPatient = (patient) => {
    setPatientId(patient.id);
    setPatientName(`${patient.first_name} ${patient.last_name}`);
    loadAllergies(patient.id);
  };

  const handleDelete = async (id) => {
    if (!confirm('Remove this allergy record?')) return;
    try { await allergiesAPI.delete(id); toast.success('Allergy removed'); loadAllergies(patientId); }
    catch { toast.error('Failed'); }
  };

  const filteredPatients = allPatients.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search)
  );

  return (
    <div className="space-y-5" data-testid="allergies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Patient Allergies</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track and manage patient allergies & contraindications</p>
        </div>
        {patientId && (
          <Button onClick={() => setShowModal(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-allergy-btn">
            <Plus size={16} className="mr-1.5" /> Add Allergy
          </Button>
        )}
      </div>

      {/* Patient Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <Label className="text-sm font-semibold text-slate-700 mb-2 block">Select Patient</Label>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search patient by name or phone..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 h-11" data-testid="patient-search" />
        </div>
        {search && (
          <div className="mt-2 max-h-48 overflow-y-auto border border-slate-100 rounded-lg">
            {filteredPatients.slice(0, 10).map(p => (
              <button key={p.id} onClick={() => { selectPatient(p); setSearch(''); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0"
                data-testid={`patient-${p.id}`}>
                <div className="h-8 w-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold">
                  {(p.first_name?.[0] || '')}{(p.last_name?.[0] || '')}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{p.first_name} {p.last_name}</p>
                  <p className="text-xs text-slate-400">{p.phone || p.email || ''}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Patient Header */}
      {patientId && (
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl border border-teal-100 p-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-teal-600 text-white flex items-center justify-center text-lg font-bold">
            {patientName.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900">{patientName}</p>
            <p className="text-xs text-slate-500">{allergies.length} allergies on record</p>
          </div>
          {allergies.some(a => a.severity === 'Severe' || a.severity === 'Life-threatening') && (
            <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">
              <AlertTriangle size={14} /> High-risk allergies
            </div>
          )}
        </div>
      )}

      {/* Allergies List */}
      {patientId && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading allergies...</div>
          ) : allergies.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ShieldAlert size={40} className="mx-auto mb-3 opacity-30" />
              <p>No allergies recorded for this patient</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allergies.map(a => {
                const Icon = TYPE_ICONS[a.allergy_type] || AlertTriangle;
                return (
                  <div key={a.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 transition-all" data-testid={`allergy-${a.id}`}>
                    <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', SEV_STYLES[a.severity] || SEV_STYLES.Moderate)}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800">{a.allergen}</p>
                      <p className="text-xs text-slate-500">{a.allergy_type} allergy &middot; {a.reaction || 'No reaction specified'}</p>
                    </div>
                    <span className={cn('text-[10px] px-2.5 py-1 rounded-full font-bold', SEV_STYLES[a.severity] || SEV_STYLES.Moderate)}>
                      {a.severity}
                    </span>
                    <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AllergyModal open={showModal} onClose={() => setShowModal(false)} patientId={patientId} hospitalId={hospital?.id} onSaved={() => loadAllergies(patientId)} />
    </div>
  );
};

const AllergyModal = ({ open, onClose, patientId, hospitalId, onSaved }) => {
  const [form, setForm] = useState({ allergen: '', allergy_type: 'Drug', severity: 'Moderate', reaction: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.allergen) { toast.error('Allergen name required'); return; }
    setSaving(true);
    try {
      await allergiesAPI.create({ ...form, patient_id: patientId, hospital_id: hospitalId });
      toast.success('Allergy added');
      onSaved(); onClose();
      setForm({ allergen: '', allergy_type: 'Drug', severity: 'Moderate', reaction: '', notes: '' });
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Allergy</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label>Allergen Name *</Label><Input value={form.allergen} onChange={e => up('allergen', e.target.value)} placeholder="e.g. Penicillin, Peanuts" data-testid="allergen-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.allergy_type} onValueChange={v => up('allergy_type', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => up('severity', v)}><SelectTrigger data-testid="severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Reaction</Label><Input value={form.reaction} onChange={e => up('reaction', e.target.value)} placeholder="e.g. Rash, Anaphylaxis" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => up('notes', e.target.value)} placeholder="Additional notes" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-allergy-btn">{saving ? 'Saving...' : 'Add Allergy'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AllergiesPage;
